import React, { useState } from "react";
import "./HealthLocator.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle
} from "react-leaflet";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png"
});

function HealthLocator() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState([]);
  const [error, setError] = useState("");
  const [radius, setRadius] = useState(5000);
  const [userLocation, setUserLocation] = useState(null);
  const [userLocationName, setUserLocationName] = useState("");

  const categories = [
    { label: "Hospitals", value: "hospital" },
    { label: "Clinics", value: "clinic" },
    { label: "Doctors", value: "doctors" },
    { label: "Pharmacies", value: "pharmacy" }
  ];

  const [selectedCategories, setSelectedCategories] = useState(
    categories.map((c) => c.value)
  );

  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
  };

  const fetchFacilities = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported.");
      return;
    }

    setLoading(true);
    setError("");
    setPlaces([]);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setUserLocation([lat, lon]);

        try {
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
          );
          const geoData = await geoRes.json();
          setUserLocationName(geoData.display_name);

          let queryParts = selectedCategories
            .map(
              (cat) =>
                `node["amenity"="${cat}"](around:${radius},${lat},${lon});`
            )
            .join("");

          const query = `[out:json];(${queryParts});out;`;

          const response = await fetch(
            "https://overpass-api.de/api/interpreter",
            {
              method: "POST",
              body: query
            }
          );

          const data = await response.json();

          const enriched = data.elements.map((el) => ({
            ...el,
            distance: haversine(lat, lon, el.lat, el.lon)
          }));

          enriched.sort((a, b) => a.distance - b.distance);

          setPlaces(enriched);
        } catch (err) {
          setError("Unable to fetch facilities.");
        }

        setLoading(false);
      },
      () => {
        setError("Location permission denied.");
        setLoading(false);
      }
    );
  };

  return (
    <div className="health-wrapper">
      <button
        className="health-main-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        📍 Advanced Health Facility Locator
      </button>

      {isOpen && (
        <div className="health-panel">

          <div className="health-controls">
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            >
              <option value={1000}>1 km</option>
              <option value={3000}>3 km</option>
              <option value={5000}>5 km</option>
              <option value={10000}>10 km</option>
              <option value={50000}>50 km</option>
              <option value={100000}>100 km</option>
            </select>

            {categories.map((cat) => (
              <label key={cat.value}>
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(cat.value)}
                  onChange={() =>
                    setSelectedCategories((prev) =>
                      prev.includes(cat.value)
                        ? prev.filter((c) => c !== cat.value)
                        : [...prev, cat.value]
                    )
                  }
                />
                {cat.label}
              </label>
            ))}

            <button onClick={fetchFacilities} className="search-btn">
              🔍 Locate
            </button>
          </div>

          {loading && <div className="loading">Scanning facilities...</div>}
          {error && <div className="error">{error}</div>}

          {userLocation && (
            <div className="map-container">
              <MapContainer
                center={userLocation}
                zoom={12}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <Marker position={userLocation}>
                  <Popup>📍 You are here</Popup>
                </Marker>

                <Circle
                  center={userLocation}
                  radius={radius}
                  pathOptions={{ color: "blue" }}
                />

                {places.map((place, i) => (
                  <Marker key={i} position={[place.lat, place.lon]}>
                    <Popup>
                      <strong>{place.tags?.name || "Unnamed"}</strong>
                      <br />
                      Type: {place.tags?.amenity}
                      <br />
                      Distance: {place.distance} km
                      <br />
                      <a
                        href={`https://www.google.com/maps?q=${place.lat},${place.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Maps
                      </a>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          <div className="facility-grid">
            {places.map((place, index) => (
              <div key={index} className="facility-card">
                <h4>{place.tags?.name || "Unnamed Facility"}</h4>
                <p>Type: {place.tags?.amenity}</p>
                <p>Distance: {place.distance} km</p>

                <a
                  href={`https://www.google.com/maps?q=${place.lat},${place.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🗺 Open in Maps
                </a>
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}

export default HealthLocator;